import { useEffect } from 'react'
import Visualization from './Visualization'
import fetchData from '../service/apiService'
import {
  setDefaultFrequency,
  setDefaultUnitMeasure,
  setDisaggregationData,
  setIndicatorCode,
  setIndicatorMetadata,
  setRecentYearSelection,
  setSelectedComparison,
  setSelectedCountries,
  setSelectedCountriesForBar,
  setSelectedFrequency,
  setSelectedOnlyShow,
  setSelectedUnitMeasure,
  setUnitMeasureName,
  setBoundaryType,
  setAvailableBoundaryTypes,
  setDimensionsData,
  setIsIndicatorMetadataCompleted,
  setIndicatorSpecificRegions,
  setRefGroupings,
} from './indicatorProfileSlice'
import { useDispatch, useSelector } from 'react-redux'
import {
  formatCountriesForQueryParams,
  getAPIHeaders,
  getDataByDimension,
  getEndpointFromConfig,
  getSuppressedCountries,
  getUnitMeasureItems,
  isMultipleFrequencyExist,
  isMultipleUnitMeasureExist,
  getAvailableBoundaryTypes,
} from '../common/utils/helper'
import {
  QueryParams,
  chartTypes,
  disaggregations,
  endpointLabels,
  periodicity,
} from '../common/utils/enum'
import useQueryParams from '../common/hooks/useQueryParams'
import {
  API_NOT_FOUND,
  API_SUCCESS_CODE,
  NONE,
  WORLD_DEFAULT,
} from '../common/utils/constants'

const IndicatorProfileWrapper = () => {
  const dispatch = useDispatch()
  const { setQueryParam, getQueryParam, deleteQueryParams } = useQueryParams()
  const { featureProps, grouping, endpointList } = useSelector(
    (state) => state.common
  )
  
  const {
    indicatorMetadata,
    disaggregationCountries,
    isDisaggregationCompleted,
    countryHierarchy,
    isIndicatorMetadataCompleted,
    countryListForIndicator,
    isCountryListLoaded,
  } = useSelector((state) => state.indicatorProfile)

  const {
    site,
    apiKeyName,
    apiKeyValue,
    excludedUnitMeasuresForMap,
  } = featureProps
const selector = getQueryParam(QueryParams.IndicatorID)
  const headers = getAPIHeaders({
    apiKeyName,
    apiKeyValue,
  })

  const fetchAdminMetadata = async (portalMetadata) => {
    try {
      const request = {
        ids: [selector],
        type: 'indicator',
      }
      const adminMetadataResponse = await fetchData(
        `${
          getEndpointFromConfig(endpointList, endpointLabels.AdminMetadata)
            .apiEndpoint
        }`,
        'POST',
        request,
        headers
      )
      if (
        adminMetadataResponse?.code === API_SUCCESS_CODE &&
        adminMetadataResponse?.data?.[0]?.admin_metadata
      ) {
        dispatch(
          setIndicatorMetadata({
            ...portalMetadata,
            admin_metadata: adminMetadataResponse.data[0].admin_metadata,
          })
        )
      }
    } catch (error) {
      console.error('Error fetching admin metadata:', error)
    } finally {
      dispatch(setIsIndicatorMetadataCompleted(true))
    }
  }

  useEffect(() => {
        if (!selector || !endpointList || endpointList.length === 0) {
      return
    }
    const fetchMetadata = async () => {
      try {
        const metadataResponse = await fetchData(
          `${
            getEndpointFromConfig(endpointList, endpointLabels.PortalMetadata)
              .apiEndpoint
          }?type=indicator&id=${selector}`,
          'GET',
          null,
          headers
        ).catch((error) => {
          if (
            error?.status === API_NOT_FOUND.code &&
            error?.response?.data?.message === API_NOT_FOUND.message
          ) {
            //window.location.href = pageNotFoundURL
          }
          throw new Error('Error fetching portal metadata:', error)
        })

        if (
          !metadataResponse ||
          !metadataResponse?.code === API_SUCCESS_CODE ||
          !metadataResponse.data
        ) {
          // window.location.href = pageNotFoundURL
          return // Exit function to prevent further execution
        }
        let portalMetadata = metadataResponse.data
        delete portalMetadata?.admin_metadata
        dispatch(setIndicatorMetadata(portalMetadata || {}))
        fetchAdminMetadata(portalMetadata)
      } catch (error) {
        console.error('Error fetching metadata:', error)
      }
    }

    fetchMetadata()
    if (
      getQueryParam(QueryParams.RecentYearSelection) &&
      getQueryParam(QueryParams.RecentYearSelection) === 'false'
    ) {
      dispatch(setRecentYearSelection(false))
    }
    if (
      (!getQueryParam(QueryParams.View) ||
        getQueryParam(QueryParams.View) === chartTypes.Trend ||
        getQueryParam(QueryParams.View) === chartTypes.Bar ||
        getQueryParam(QueryParams.View) === chartTypes.StackedBar ||
        getQueryParam(QueryParams.View) === chartTypes.Pie) &&
      getQueryParam(QueryParams.ComparisonBy) &&
      getQueryParam(QueryParams.ComparisonBy) !== NONE
    ) {
      dispatch(setSelectedComparison(getQueryParam(QueryParams.ComparisonBy)))
    } else if (
      getQueryParam(QueryParams.View) === chartTypes.DataTable &&
      !getQueryParam(QueryParams.ComparisonBy)
    ) {
      dispatch(setSelectedComparison(''))
    }
    if (
      getQueryParam(QueryParams.OnlyShow) &&
      (getQueryParam(QueryParams.View) === chartTypes.Map ||
        getQueryParam(QueryParams.View) === chartTypes.Correlation)
    ) {
      dispatch(
        setSelectedOnlyShow({
          chartType: getQueryParam(QueryParams.View),
          selectedOnlyShow: getQueryParam(QueryParams.OnlyShow),
        })
      )
    }
  }, [featureProps, endpointList, selector])

  useEffect(() => {
    const fetchDimensionsAndRefGroupings = async (datasetID) => {
      try {
        const dimensionsPayload = {
          database_id: datasetID,
          indicator_id: selector.trim().toUpperCase(),
          isLatestData: true,
        }

        const dimensionsEndpoint = getEndpointFromConfig(
          endpointList,
          endpointLabels.Dimensions
        )?.apiEndpoint
        const codelistRefGroupingsEndpoint = getEndpointFromConfig(
          endpointList,
          endpointLabels.CodelistRefGroupings
        )?.apiEndpoint

        if (!dimensionsEndpoint) {
          console.warn('Dimensions endpoint not configured')
          return
        }
        if (!codelistRefGroupingsEndpoint) {
          console.warn('Codelist Ref Groupings endpoint not configured')
          return
        }

        let [dimensionsResponse, codelistRefGroupingsResponse] =
          await Promise.allSettled([
            fetchData(dimensionsEndpoint, 'POST', dimensionsPayload, headers),
            fetchData(codelistRefGroupingsEndpoint, 'GET', null, headers),
          ])
        dimensionsResponse = dimensionsResponse?.value || null
        codelistRefGroupingsResponse =
          codelistRefGroupingsResponse?.value || null

        const refGroupings =
          codelistRefGroupingsResponse?.data?.data ||
          codelistRefGroupingsResponse?.data ||
          []
        if (datasetID === 'IFC_GB') {
          dispatch(
            setRefGroupings(
              refGroupings?.codelists?.[0]?.codes?.map((item) => item.id) ?? []
            )
          )
        }

        if (dimensionsResponse?.dimensions) {
          const regions = getDataByDimension(
            dimensionsResponse?.dimensions,
            disaggregations.Region
          )
          dispatch(setIndicatorSpecificRegions(regions))
        }

        if (getQueryParam(QueryParams.CountrySelection)) {
          // added logic to handle when country query param has WLD
          let countries = getQueryParam(QueryParams.CountrySelection).split(',')
          countries =
            countries.length > 0
              ? countries.filter((item) => item !== WORLD_DEFAULT.id)
              : []
          const suppressedCountries =
            indicatorMetadata?.admin_metadata?.[site]?.visualization
              ?.suppression
          // const suppressedCountries = 'AUT'
          countries = countries.filter(
            (item) =>
              !getSuppressedCountries(suppressedCountries).includes(item)
          )
          if (
            getQueryParam(QueryParams.CountrySelection).toUpperCase() ===
            WORLD_DEFAULT.id
          ) {
            deleteQueryParams([QueryParams.CountrySelection])
          } else if (
            getQueryParam(QueryParams.CountrySelection)
              .split(',')
              .map((item) => item.toUpperCase())
              .includes(WORLD_DEFAULT.id)
          ) {
            setQueryParam(
              QueryParams.CountrySelection,
              formatCountriesForQueryParams(countries)
            )
          }
          if (countries.length > 0) {
            if (getQueryParam(QueryParams.View) === chartTypes.Bar) {
              dispatch(setSelectedCountriesForBar(countries))
              const filteredCountries = countries.filter(
                (item) => item !== 'all'
              )
              dispatch(setSelectedCountries(filteredCountries))
            } else {
              dispatch(setSelectedCountries(countries))
              dispatch(setSelectedCountriesForBar(['all', ...countries]))
            }
          }
        }

        if (
          dimensionsResponse?.dimensions &&
          isMultipleUnitMeasureExist(dimensionsResponse)
        ) {
          let unitMeasures = getDataByDimension(
            dimensionsResponse?.dimensions,
            disaggregations.UnitMeasure
          )
          if (datasetID === 'IFC_GB') {
            const desiredOrder = ['USD', 'PT_GDP', 'PT_FI']
            unitMeasures.sort((a, b) => {
              const indexA = desiredOrder.indexOf(a?.code)
              const indexB = desiredOrder.indexOf(b?.code)
              const orderA = indexA === -1 ? desiredOrder.length : indexA
              const orderB = indexB === -1 ? desiredOrder.length : indexB
              return orderA - orderB
            })
          }

          // If chart type is map, set default unit measure to the second one in the list if first one is part of excluded items
          const defaultUnitMeasure =
            getQueryParam(QueryParams.View) &&
            getQueryParam(QueryParams.View) !== chartTypes.Map
              ? unitMeasures?.[0]
              : excludedUnitMeasuresForMap
                    ?.split(',')
                    ?.includes(unitMeasures?.[0]?.code)
                ? unitMeasures?.[1]
                : unitMeasures?.[0]
          const defaultUnitMeasureId = defaultUnitMeasure?.code
          const defaultUnitMeasureName = defaultUnitMeasure?.description
          if (getQueryParam(QueryParams.UnitMeasure)) {
            dispatch(
              setSelectedUnitMeasure(getQueryParam(QueryParams.UnitMeasure))
            )

            const queryParamsUnitMeasureName = getUnitMeasureItems(
              dimensionsResponse
            )?.find(
              (item) => item.code === getQueryParam(QueryParams.UnitMeasure)
            )?.description

            dispatch(setUnitMeasureName(queryParamsUnitMeasureName))
          } else {
            dispatch(setSelectedUnitMeasure(defaultUnitMeasureId))
            dispatch(setUnitMeasureName(defaultUnitMeasureName))
          }
          dispatch(setDefaultUnitMeasure(defaultUnitMeasureId))
        }

        const unitMeasures = getDataByDimension(
          dimensionsResponse?.dimensions,
          disaggregations.UnitMeasure
        )
        if (unitMeasures?.length === 1) {
          const defaultUnitMeasure = unitMeasures?.[0]?.code
          const defaultUnitMeasureName = unitMeasures?.[0]?.description
          dispatch(setSelectedUnitMeasure(defaultUnitMeasure))
          dispatch(setUnitMeasureName(defaultUnitMeasureName))
        }
        if (isMultipleFrequencyExist(dimensionsResponse?.dimensions)) {
          const annualFrequency = getDataByDimension(
            dimensionsResponse?.dimensions,
            disaggregations.Frequency
          ).find((item) => item.code === periodicity.Annual)
          const defaultFrequency = annualFrequency
            ? annualFrequency.code
            : getDataByDimension(
                dimensionsResponse?.dimensions,
                disaggregations.Frequency
              )?.[0]?.code
          if (getQueryParam(QueryParams.Frequency)) {
            dispatch(setSelectedFrequency(getQueryParam(QueryParams.Frequency)))
          } else {
            dispatch(setSelectedFrequency(defaultFrequency))
          }
          dispatch(setDefaultFrequency(defaultFrequency))
        } else {
          dispatch(
            setSelectedFrequency(
              getDataByDimension(
                dimensionsResponse?.dimensions,
                disaggregations.Frequency
              )?.[0]?.code
            )
          )
        }

        dispatch(setIndicatorCode(selector))
        dispatch(
          setDisaggregationData({
            disaggregation: dimensionsResponse,
            visualization:
              indicatorMetadata?.admin_metadata?.[site]?.visualization,
            refGroupingsCodelist: refGroupings?.codelists?.[0]?.codes ?? [],
          })
        )

        if (
          dimensionsResponse?.dimensions_filtered &&
          dimensionsResponse.dimensions_filtered.length > 0
        ) {
          const availableFilters = {}
          dimensionsResponse.dimensions_filtered.forEach((item) => {
            availableFilters[item.field_name] = item.field_value?.map(
              (field) => field?.code
            )
          })
          dispatch(setDimensionsData({ availableFilters }))
        }
      } catch (error) {
        console.error('Error fetching dimensions data:', error)
      }
    }

    if (isIndicatorMetadataCompleted) {
      let datasetID = indicatorMetadata?.series_description?.database_id || ''

      if (datasetID) {
        fetchDimensionsAndRefGroupings(datasetID)
      }
    }
  }, [isIndicatorMetadataCompleted, endpointList, selector])

  useEffect(() => {
    if (
      isDisaggregationCompleted &&
      isCountryListLoaded &&
      grouping?.['HCL_WB_REGIONS'] &&
      countryHierarchy &&
      Object.keys(countryHierarchy).length > 0
    ) {
      const availableTypes = getAvailableBoundaryTypes({
        disaggregationCountries,
        countryList: countryListForIndicator,
        grouping,
        countryHierarchy,
      })
      dispatch(setAvailableBoundaryTypes(availableTypes))

      const mapTypeFromUrl = getQueryParam(QueryParams.MapType)
      const defaultBoundaryType =
        mapTypeFromUrl && availableTypes.includes(mapTypeFromUrl)
          ? mapTypeFromUrl
          : availableTypes.length > 0
            ? availableTypes[0]
            : null
      dispatch(setBoundaryType(defaultBoundaryType))
    }
  }, [
    disaggregationCountries,
    isCountryListLoaded,
    isDisaggregationCompleted,
    grouping,
    countryHierarchy,
  ])

  return (
    <>
      <Visualization />
    </>
  )
}
export default IndicatorProfileWrapper
